import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Play, Pause, RotateCcw, Plus, Minus, Settings, Volume2, VolumeX, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FloatingTimer from '../components/FloatingTimer';
import { ThemedBackground } from '../components/ThemedBackground';
import { useTheme } from '../lib/theme-provider';

interface TimerPreset {
  label: string;
  seconds: number;
}

const PRESETS: TimerPreset[] = [
  { label: '30秒', seconds: 30 },
  { label: '1分钟', seconds: 60 },
  { label: '2分钟', seconds: 120 },
  { label: '3分钟', seconds: 180 },
  { label: '5分钟', seconds: 300 },
];

export default function SpeechTimer() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [duration, setDuration] = useState(60);
  const [remaining, setRemaining] = useState(60);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [customMinutes, setCustomMinutes] = useState(1);
  const [customSeconds, setCustomSeconds] = useState(0);
  const [showFloatingTimer, setShowFloatingTimer] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: AudioContext }).webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.2);
    } catch {
      // Audio not supported
    }
  }, [soundEnabled]);

  // 播放持续闹铃声（类似手机闹钟，持续4秒）
  const playFinishSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: AudioContext }).webkitAudioContext)();
      const startTime = ctx.currentTime;
      const duration = 4;
      
      // 创建主振荡器（方波产生类似电子闹钟的声音）
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(880, startTime);
      
      // 模拟"哔-哔-哔"的闹钟节奏
      const beatDuration = 0.15;
      const pauseDuration = 0.1;
      const cycleDuration = beatDuration + pauseDuration;
      
      for (let t = 0; t < duration; t += cycleDuration) {
        const time = startTime + t;
        gainNode.gain.setValueAtTime(0.4, time);
        gainNode.gain.setValueAtTime(0, time + beatDuration);
      }
      
      // 最后渐弱
      gainNode.gain.setValueAtTime(0.4, startTime + duration - 0.3);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
      
      // 添加和声
      const oscillator2 = ctx.createOscillator();
      const gainNode2 = ctx.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(ctx.destination);
      
      oscillator2.type = 'sine';
      oscillator2.frequency.setValueAtTime(440, startTime);
      
      for (let t = 0; t < duration; t += cycleDuration) {
        const time = startTime + t;
        gainNode2.gain.setValueAtTime(0.2, time);
        gainNode2.gain.setValueAtTime(0, time + beatDuration);
      }
      
      gainNode2.gain.setValueAtTime(0.2, startTime + duration - 0.3);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator2.start(startTime);
      oscillator2.stop(startTime + duration);
    } catch {
      // Audio not supported
    }
  }, [soundEnabled]);

  useEffect(() => {
    if (isRunning && !isPaused) {
      timerRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            playFinishSound();
            return 0;
          }
          if (prev <= 10 && prev > 1) {
            playBeep();
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, isPaused, playBeep, playFinishSound]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    if (remaining > 0) {
      setIsRunning(true);
      setIsPaused(false);
    }
  };

  const pauseTimer = () => {
    setIsPaused(!isPaused);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setRemaining(duration);
  };

  const adjustTime = (delta: number) => {
    if (isRunning) return;
    const newDuration = Math.max(10, Math.min(600, duration + delta));
    setDuration(newDuration);
    setRemaining(newDuration);
  };

  const applyCustomTime = () => {
    const newDuration = customMinutes * 60 + customSeconds;
    if (newDuration > 0 && newDuration <= 600) {
      setDuration(newDuration);
      setRemaining(newDuration);
      setShowSettings(false);
    }
  };

  const selectPreset = (seconds: number) => {
    setDuration(seconds);
    setRemaining(seconds);
  };

  const openFloatingTimer = () => {
    setShowFloatingTimer(true);
  };

  const progress = duration > 0 ? ((duration - remaining) / duration) * 100 : 0;
  const isLowTime = remaining <= 10 && remaining > 0 && isRunning;
  const isCritical = remaining <= 5 && remaining > 0 && isRunning;

  return (
    <ThemedBackground pageName="speech-timer">
    <div className="min-h-screen p-5 pt-[72px] pb-8">
      {/* V189: 固定头部 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-3" style={{ backgroundColor: theme.colors['bg-primary'], borderBottom: `1px solid ${theme.colors['border-secondary'] || '#5A4BFF'}35` }}>
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white hover:bg-avalon-purple transition-colors" style={{ backgroundColor: theme.colors['bg-card'] }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-white">发言倒计时</h1>
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
            soundEnabled ? 'text-avalon-bg-dark' : 'text-avalon-text'
          }`} style={{ backgroundColor: soundEnabled ? theme.colors['accent-gold'] : theme.colors['bg-card'] }}
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white hover:bg-avalon-purple transition-colors" style={{ backgroundColor: theme.colors['bg-card'] }}
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Floating Timer Button */}
      <div className="mb-6 flex justify-end">
        <button
          onClick={openFloatingTimer}
          className="avalon-btn avalon-btn-secondary flex items-center gap-2 py-2 px-4"
        >
          <ExternalLink className="w-4 h-4" />
          打开悬浮计时器
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="avalon-card-lg mb-6 animate-in slide-in-from-top-2">
          <h3 className="text-white font-semibold mb-4">自定义时长</h3>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <label className="text-avalon-text text-xs mb-1 block">分钟</label>
              <input
                type="number"
                min="0"
                max="10"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
                className="w-full bg-avalon-bg-dark rounded-lg px-3 py-2 text-white text-center"
              />
            </div>
            <div className="text-avalon-text text-2xl">:</div>
            <div className="flex-1">
              <label className="text-avalon-text text-xs mb-1 block">秒钟</label>
              <input
                type="number"
                min="0"
                max="59"
                value={customSeconds}
                onChange={(e) => setCustomSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                className="w-full bg-avalon-bg-dark rounded-lg px-3 py-2 text-white text-center"
              />
            </div>
          </div>
          <button onClick={applyCustomTime} className="w-full avalon-btn py-3">
            应用设置
          </button>
        </div>
      )}

      {/* Presets */}
      {!isRunning && (
        <div className="mb-6">
          <div className="text-avalon-text text-sm mb-3">快速选择</div>
          <div className="flex gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset.seconds}
                onClick={() => selectPreset(preset.seconds)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  duration === preset.seconds
                    ? 'bg-avalon-gold text-avalon-bg-dark'
                    : 'bg-avalon-card text-avalon-text hover:bg-avalon-purple'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timer Display */}
      <div className="avalon-card-lg mb-6">
        {/* Progress Ring */}
        <div className="relative w-48 h-48 mx-auto mb-6">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="96"
              cy="96"
              r="88"
              stroke="#2A1A4A"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="96"
              cy="96"
              r="88"
              stroke={isCritical ? '#ef4444' : isLowTime ? '#F5D74A' : '#F5D74A'}
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 88}`}
              strokeDashoffset={`${2 * Math.PI * 88 * (1 - progress / 100)}`}
              className="transition-all duration-1000"
              style={{
                filter: isLowTime ? 'drop-shadow(0 0 10px rgba(245, 215, 74, 0.5))' : 'none',
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`text-5xl font-bold font-mono transition-all ${
                isCritical ? 'text-red-400 scale-110' : 'text-avalon-gold'
              } ${isLowTime ? 'animate-pulse' : ''}`}
            >
              {formatTime(remaining)}
            </div>
          </div>
        </div>

        {/* Time Adjustment */}
        {!isRunning && (
          <div className="flex items-center justify-center gap-6 mb-6">
            <button
              onClick={() => adjustTime(-10)}
              className="w-14 h-14 rounded-xl bg-avalon-purple flex items-center justify-center text-white text-2xl font-bold hover:bg-avalon-purple-light transition-colors"
            >
              <Minus className="w-6 h-6" />
            </button>
            <div className="text-avalon-text text-sm">
              {Math.floor(duration / 60)}分{duration % 60}秒
            </div>
            <button
              onClick={() => adjustTime(10)}
              className="w-14 h-14 rounded-xl bg-avalon-purple flex items-center justify-center text-white text-2xl font-bold hover:bg-avalon-purple-light transition-colors"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Control Buttons */}
        <div className="flex gap-3">
          {!isRunning ? (
            <button
              onClick={startTimer}
              disabled={remaining === 0}
              className="flex-1 avalon-btn py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Play className="w-6 h-6" />
              开始
            </button>
          ) : (
            <button
              onClick={pauseTimer}
              className="flex-1 avalon-btn avalon-btn-secondary py-4 text-lg flex items-center justify-center gap-2"
            >
              {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              {isPaused ? '继续' : '暂停'}
            </button>
          )}
          <button
            onClick={resetTimer}
            className="avalon-btn avalon-btn-secondary px-6 py-4 flex items-center justify-center"
          >
            <RotateCcw className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {isRunning && (
        <div className="text-center">
          {isCritical ? (
            <p className="text-red-400 font-semibold animate-pulse">时间即将结束！</p>
          ) : isLowTime ? (
            <p className="text-avalon-gold font-semibold">时间所剩无几</p>
          ) : (
            <p className="text-avalon-text">发言进行中...</p>
          )}
        </div>
      )}

      {remaining === 0 && !isRunning && (
        <div className="text-center avalon-card-lg">
          <div className="text-4xl mb-2">⏰</div>
          <h3 className="text-white font-bold text-lg mb-2">时间到！</h3>
          <p className="text-avalon-text mb-4">发言时间已结束</p>
          <button onClick={resetTimer} className="avalon-btn px-8">
            重新开始
          </button>
        </div>
      )}

      {/* Floating Timer */}
      <FloatingTimer isVisible={showFloatingTimer} onClose={() => setShowFloatingTimer(false)} />
    </div>
    </ThemedBackground>
  );
}
